import React, { useState, useEffect, useRef } from 'react';
import { LivePinService } from '../../service/livePin.service';
import { ParsedVehicleData } from '../../service/interfaces/LivePin.interface';

interface LivePinTrackerProps {
    livePinUrl: string;
    refreshInterval?: number; // in milliseconds, default 30000 (30 seconds)
    onDataUpdate?: (data: ParsedVehicleData) => void;
    onError?: (error: string) => void;
}

const LivePinTracker: React.FC<LivePinTrackerProps> = ({
    livePinUrl,
    refreshInterval = 60000,
    onDataUpdate,
    onError
}) => {
    const [vehicleData, setVehicleData] = useState<ParsedVehicleData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    const livePinService = new LivePinService();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const fetchVehicleData = async () => {
        if (!livePinUrl || !livePinService.isValidLivePinUrl(livePinUrl)) {
            const errorMsg = 'Invalid LivePin URL format';
            setError(errorMsg);
            onError?.(errorMsg);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const rawData = await livePinService.fetchVehicleData(livePinUrl);

            if (rawData) {
                const parsedData = livePinService.parseVehicleData(rawData);
                setVehicleData(parsedData);
                setLastUpdate(new Date());
                setIsConnected(true);
                onDataUpdate?.(parsedData);
            } else {
                throw new Error('No data received from LivePin API');
            }
        } catch (err) {
            const errorMsg = `Failed to fetch vehicle data: ${err}`;
            setError(errorMsg);
            setIsConnected(false);
            onError?.(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (livePinUrl) {
            // Initial fetch
            fetchVehicleData();

            // Set up polling
            intervalRef.current = setInterval(fetchVehicleData, refreshInterval);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [livePinUrl, refreshInterval]);

    const formatTimestamp = (timestamp: number): string => {
        return new Date(timestamp).toLocaleString();
    };

    const getStatusColor = (status: string): string => {
        switch (status.toUpperCase()) {
            case 'PARKED':
                return 'bg-yellow-100 text-yellow-800';
            case 'RUNNING':
                return 'bg-green-100 text-green-800';
            case 'MOVING':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (!livePinUrl) {
        return (
            <div className="p-4 bg-gray-100 rounded-lg">
                <p className="text-gray-600">No LivePin URL provided</p>
            </div>
        );
    }

    return (
        <div className="livepin-tracker bg-white rounded-lg shadow-md p-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                    LivePin Vehicle Tracker
                </h3>
                <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-600">
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </span>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-2 text-gray-600">Loading vehicle data...</span>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                        <div className="text-red-500 mr-2">⚠️</div>
                        <div>
                            <h4 className="text-red-800 font-medium">Connection Error</h4>
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Vehicle Data Display */}
            {vehicleData && !loading && (
                <div className="space-y-4">
                    {/* Vehicle Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-2">
                            {vehicleData.vehicleNumber}
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Speed:</span>
                                <span className="ml-2 font-medium">{vehicleData.speed} km/h</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Status:</span>
                                <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(vehicleData.status)}`}>
                                    {vehicleData.status}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">Battery:</span>
                                <span className="ml-2 font-medium">{vehicleData.battery}%</span>
                            </div>
                            <div>
                                <span className="text-gray-600">ACC:</span>
                                <span className="ml-2 font-medium">{vehicleData.acc ? 'ON' : 'OFF'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Location Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="font-medium text-gray-800 mb-2">Location Details</h5>
                        <div className="text-sm space-y-1">
                            <div>
                                <span className="text-gray-600">Coordinates:</span>
                                <span className="ml-2 font-mono text-xs">
                                    {vehicleData.latitude.toFixed(6)}, {vehicleData.longitude.toFixed(6)}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">Address:</span>
                                <span className="ml-2 text-xs">{vehicleData.address}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Last Update:</span>
                                <span className="ml-2 text-xs">{formatTimestamp(vehicleData.timestamp)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Last Update Info */}
                    {lastUpdate && (
                        <div className="text-xs text-gray-500 text-center">
                            Data refreshed: {lastUpdate.toLocaleTimeString()}
                        </div>
                    )}
                </div>
            )}

            {/* Manual Refresh Button */}
            <div className="mt-4 flex justify-center">
                <button
                    onClick={fetchVehicleData}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? 'Refreshing...' : 'Refresh Now'}
                </button>
            </div>
        </div>
    );
};

export default LivePinTracker;
